import { useEffect, useRef, type ReactNode } from "react";
import {
  Animated,
  Easing,
  Pressable,
  StyleSheet,
  View,
  type ImageSourcePropType,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const darkCopper = require("@src/apps/moon/assets/images/brown-grainy-texture-2.jpg");

type Props = {
  children: ReactNode;
  onPress?: () => void;
  borderRadius?: number;
  padding?: number;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  backgroundImage?: ImageSourcePropType;
  animateBackground?: boolean;
  /** Stable analytics identifier for the hero this card renders. */
  action?: string;
  /** Visible copy naming the hero, logged when the card is pressed. */
  pressLabelText?: string;
};

export function TexturedHeroCard({
  children,
  onPress,
  borderRadius = 20,
  padding = 24,
  style,
  contentStyle,
  backgroundImage = darkCopper,
  animateBackground = false,
  action,
  pressLabelText,
}: Props) {
  const capturePress = usePressCapture("TexturedHeroCard");
  const drift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!animateBackground) {
      drift.setValue(0);
      return undefined;
    }

    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(drift, {
          toValue: 1,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(drift, {
          toValue: 0,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [animateBackground, drift]);

  const card = (
    <View style={[{ borderRadius, overflow: "hidden" }, style]}>
      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Animated.Image
          source={backgroundImage}
          resizeMode="cover"
          style={[
            {
              position: "absolute",
              left: animateBackground ? "-9%" : 0,
              top: animateBackground ? "-11%" : 0,
              width: animateBackground ? "118%" : "100%",
              height: animateBackground ? "122%" : "100%",
            },
            animateBackground
              ? {
                  transform: [
                    {
                      translateX: drift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [-14, 16],
                      }),
                    },
                    {
                      translateY: drift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [7, -10],
                      }),
                    },
                    {
                      scale: drift.interpolate({
                        inputRange: [0, 1],
                        outputRange: [1.02, 1.07],
                      }),
                    },
                  ],
                }
              : null,
          ]}
        />
      </View>
      <View style={[{ padding }, contentStyle]}>{children}</View>
    </View>
  );

  if (!onPress) return card;

  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(pressLabelText), action ?? "hero_card");
        onPress();
      }}
    >
      {card}
    </Pressable>
  );
}
